import { useState, type FormEvent, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { sendContact } from "../../lib/api";

type Status = "idle" | "loading" | "success" | "error";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full rounded-2xl border-[3px] border-navy bg-white px-4 py-3 text-navy placeholder-charcoal/45 outline-none transition focus:ring-2 focus:ring-teal focus:ring-offset-2 focus:ring-offset-white";

const labelCls = "mb-1.5 block font-display font-bold text-navy";

export function ContactSection({
  eyebrow = "Contact",
  title,
  sub = "Got a question the FAQ didn't answer? Send us a note.",
}: {
  eyebrow?: string;
  title?: ReactNode;
  sub?: ReactNode;
} = {}) {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Clear a validation error as soon as the user starts fixing the input.
  const clearErr = () => {
    if (status === "error") {
      setStatus("idle");
      setErrorMsg("");
    }
  };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName) {
      setStatus("error");
      setErrorMsg("Please tell us your name.");
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (!trimmedMessage) {
      setStatus("error");
      setErrorMsg("Please write a message before sending.");
      return;
    }

    setStatus("loading");
    setErrorMsg("");

    try {
      const result = await sendContact({
        name: trimmedName,
        email: trimmedEmail,
        subject: subject.trim() || undefined,
        message: trimmedMessage,
      });

      if ("notConfigured" in result) {
        setStatus("error");
        setErrorMsg(
          "We can't send messages just yet. Please try again shortly."
        );
        return;
      }

      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "Something went wrong. Please try again."
      );
    }
  }

  return (
    <section id="contact" className="relative py-20 sm:py-28 bg-cream">
      <div className="container-x">
        <div className="mx-auto max-w-3xl">
          <SectionHeading
            align="center"
            eyebrow={eyebrow}
            title={
              title ?? (
                <>
                  Press, partnerships,{" "}
                  <span className="marker-teal marker">or just hello</span>
                </>
              )
            }
            sub={sub}
          />

          <Reveal delay={4} className="mt-12">
            {status === "success" ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="card-pop flex flex-col items-center px-8 py-14 text-center"
                role="status"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-navy bg-teal-mist text-teal shadow-pop-xs">
                  <CheckCircle2 className="h-9 w-9" aria-hidden="true" />
                </span>
                <h3 className="display mt-6 text-2xl text-navy">
                  Message sent.
                </h3>
                <p className="mt-3 text-base leading-relaxed text-charcoal/70">
                  Thanks, we'll get back to you at the email you gave us.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="card-pop p-6 sm:p-8"
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className={labelCls}>
                      Name
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        clearErr();
                      }}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelCls}>
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearErr();
                      }}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-subject" className={labelCls}>
                    Subject
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    autoComplete="off"
                    placeholder="Subject (optional)"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      clearErr();
                    }}
                    className={inputCls}
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-message" className={labelCls}>
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    placeholder="How can we help?"
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      clearErr();
                    }}
                    className={`${inputCls} resize-y`}
                  />
                </div>

                {status === "error" && errorMsg && (
                  <p
                    className="mt-4 text-sm font-bold text-red-600"
                    role="alert"
                    aria-live="polite"
                  >
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2 px-7 py-3.5 text-base sm:w-auto"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    "Send message"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
